import dayjs from 'dayjs';
import { Client } from '../types/entities';
import { trimOptionalClientText } from './clientMapper';

/** Normalise une date d'anniversaire à midi UTC (évite les décalages fuseau). */
export function normalizeClientBirthDate(date: unknown): Date | null {
  if (!date) return null;
  const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date as string | number | Date);
  if (!dayjsDate.isValid()) return null;
  return new Date(
    Date.UTC(dayjsDate.year(), dayjsDate.month(), dayjsDate.date(), 12, 0, 0, 0)
  );
}

function cleanStringForFirestore(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function cleanObjectForFirestore(obj: unknown): Record<string, unknown> | null {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object' || Array.isArray(obj)) return null;
  const cleaned: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) cleaned[key] = trimmed;
      return;
    }
    cleaned[key] = value;
  });
  return Object.keys(cleaned).length > 0 ? cleaned : null;
}

export type ClientFormValues = Record<string, unknown>;

/**
 * Transforme les valeurs du formulaire Ant Design en payload client pour Firestore.
 */
export function buildClientPayloadFromFormValues(
  values: ClientFormValues
): Omit<Client, 'id' | 'businessId' | 'loyaltyBalance' | 'createdAt' | 'updatedAt'> & {
  birthDate?: Date | null;
} {
  const payload: Record<string, unknown> = {
    name: '',
    phone: '',
    email: '',
    birthDate: null,
  };

  Object.entries(values).forEach(([key, value]) => {
    // Opt-in UI seulement — ne jamais écrire sur le doc client
    if (key === 'createBonspotCard') return;
    if (key === 'birthDate') {
      payload.birthDate = value ? normalizeClientBirthDate(value) : null;
      return;
    }
    if (key === 'emergencyContact' || key === 'preferences') {
      payload[key] = cleanObjectForFirestore(value);
      return;
    }
    if (key === 'city' || key === 'district' || key === 'address') {
      payload[key] = trimOptionalClientText(value as string) ?? null;
      return;
    }
    payload[key] = cleanStringForFirestore(value);
  });

  return payload as Omit<Client, 'id' | 'businessId' | 'loyaltyBalance' | 'createdAt' | 'updatedAt'> & {
    birthDate?: Date | null;
  };
}

export function validateClientFormPayload(
  payload: Pick<Client, 'name'>
): string | null {
  const name = payload.name?.trim();
  if (!name) return 'Le nom du client est obligatoire';
  if (name.length < 2) return 'Le nom doit contenir au moins 2 caractères';
  return null;
}
